import { useEffect, useRef, useState } from 'react';
import { produce } from 'immer';
import { StationeryDraftFormat } from '@graphql/generated-base';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { withWindow } from '@shared/utils/withWindow';
import { addError } from '@shared/utils/logger';
import { useUpdateStationeryDraftProductMutation } from '@apps/card/Card.graphql.generated';
import { useUpdateStationeryDraftMutation } from '../../CardCustomizer.graphql.generated';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { useCustomizationContext } from '../../useCardCustomization';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useDraftSaverFlush } from '../../useDraftSaver';
import type { DraftJson } from '../../DraftJson.types';

type UseSwitchDraftProductArgs = Readonly<{
  draftId: string;
}>;

type SwitchTarget = Readonly<{ id: string; productType: string }>;

type SwitchProductOptions = Readonly<{
  /**
   * Applied to the config the pre-switch save writes -- so the server converts the prepared config,
   * not the one on screen. The editor state is left alone, since the switch reloads or navigates.
   */
  prepareConfig?: (config: DraftJson) => void;
  /** Called once the switch has landed and before the page navigates or reloads away. */
  onSwitched?: () => void;
}>;

const isPrintType = (productType: string) => productType === 'stationeryCard' || productType === 'magnet';

/**
 * Save the draft, move it onto another product, and land on the customizer for that product.
 *
 * Shared by the format switcher (`useProductChoiceGroup`) and the super-admin `?productId=` param
 * (`useAdminProductIdParam`). Resolves to whether a switch was started; throws if it failed.
 */
export const useSwitchDraftProduct = ({ draftId }: UseSwitchDraftProductArgs) => {
  const [draftConfig] = useCustomizationContext(d => d.config);
  const { activeProduct } = useStationeryProductPricingContext();
  const { goToCustomizeDraft, goToDigitalCustomizeDraft } = useCardsRouterContext();
  const { eventInfo } = useEventInfo();
  const flushPendingSave = useDraftSaverFlush();

  const [updateDraftProduct] = useUpdateStationeryDraftProductMutation();
  const [saveDraft] = useUpdateStationeryDraftMutation();

  const [switching, setSwitching] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const switchProduct = useEventCallback(async (targetProduct: SwitchTarget, options?: SwitchProductOptions): Promise<boolean> => {
    if (switching || targetProduct.id === activeProduct.id) return false;
    if (!eventInfo?.eventId) return false;

    setSwitching(true);
    // Held true through a same-format reload: releasing the switching lock in
    // `finally` would let a second concurrent switch fire during the reload
    // delay and corrupt the draft mid-unload.
    let keepLockForReload = false;
    try {
      // Settle the draft saver FIRST. Cancelling its throttle is not enough: a save
      // already in flight -- or the one it forces on mount, which a switch started
      // from a child's mount effect runs ahead of -- could land mid-switch and
      // overwrite the converted cardJSON with the old payload. Flushing waits out
      // anything in flight and marks the current config as written, so the saver
      // has nothing left to send.
      await flushPendingSave();

      // Deliberately not applied to the editor state: every path below reloads or
      // navigates, and a local change would only hand the saver a write that could
      // land after the switch.
      const { prepareConfig } = options ?? {};

      // Persist the latest local edits before switching formats so the BE converts
      // the most recent cardJSON, not whatever was last throttle-saved.
      await saveDraft({
        variables: { payload: { id: draftId, eventId: eventInfo.eventId, cardJSON: prepareConfig ? produce(draftConfig, prepareConfig) : draftConfig } },
      });

      let result;
      try {
        result = await updateDraftProduct({
          variables: { data: { id: draftId, productId: targetProduct.id } },
        });
      } catch (error) {
        // The prepared config is already saved, but the draft never left its product -- an order in
        // progress, or a network failure. Put the original back, or a draft still on its old product
        // keeps a design meant for the new one. The editor state was never changed, so `draftConfig`
        // is still the original.
        if (prepareConfig) {
          try {
            await saveDraft({ variables: { payload: { id: draftId, eventId: eventInfo.eventId, cardJSON: draftConfig } } });
          } catch (restoreError) {
            addError(restoreError, { context: 'useSwitchDraftProduct.restoreSave', draftId });
          }
        }
        throw error;
      }

      options?.onSwitched?.();

      const newFormat = result.data?.updateStationeryDraftProduct.format;
      const sameFormatSwitch = isPrintType(activeProduct.productType) === isPrintType(targetProduct.productType);
      if (sameFormatSwitch) {
        // Same-format switch (postcard ↔ magnet, both print): navigation lands
        // on the already-mounted customizer, and its draft query is fetchPolicy
        // 'no-cache' — the mutation result cannot update it, so the stale
        // product would keep driving steps, previews, and checkout. Hard-reload;
        // the switch is fully persisted server-side. The short delay lets the
        // telemetry request above flush — an immediate reload aborts it.
        keepLockForReload = true;
        withWindow(global => {
          global.setTimeout(() => global.location.reload(), 300);
        });
        return true;
      }
      if (newFormat === StationeryDraftFormat.print) {
        goToCustomizeDraft(draftId);
      } else if (newFormat === StationeryDraftFormat.digital) {
        goToDigitalCustomizeDraft(draftId);
      }
      return true;
    } finally {
      if (isMountedRef.current && !keepLockForReload) {
        setSwitching(false);
      }
    }
  });

  return { switchProduct, isSwitching: switching };
};
